import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonText,
  IonToolbar,
  isPlatform,
} from '@ionic/react';
import {
  addCircleOutline,
  ellipsisHorizontalCircle,
  search,
} from 'ionicons/icons';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useHasPermission } from '../../api/authorization.provider';
import {
  ListTeamsInput,
  ListTeamsQuery,
  Team,
  TeamList,
  useGetTenantQuery,
  useListTeamsQuery,
} from '../../api/graphql/generated';
import { useListBookmarks } from '../../api/hooks/bookmark.hooks';
import { useUnreadNotifications } from '../../api/hooks/event-notifications.hooks';
import { useHasFeature } from '../../api/hooks/feature.hooks';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { useCurrentUser } from '../../api/user.hooks';
import { trackSearchResultClicked } from '../../config/analytics';
import { useIntercomUnreadNotificationCount } from '../../config/intercom';
import i18n from '../../i18n';
import { useBeaconRouter } from '../../route/route.hooks';
import { RouteBuilder } from '../../route/route.types';
import { sleep } from '../../util/async';
import { Avatar } from '../avatar/avatar';
import { IonItem } from '../ionic/IonItem';
import { useOnboardingChecklistRemaining } from '../onboarding/onboarding-checklist.hook';
import {
  SupportOpenSource,
  useSupportContext,
} from '../support/support.context';
import { SearchRowRenderer, useSearchPopover } from '../ui/searchbar/searchbar';
import { WorkspaceSwitcherModal } from '../workspace/workspace-switcher';
import './menu.css';

interface MenuPage {
  cssId?: string;
  key: string;
  title: string | ReactElement;
  path: string;
  badgeNumber?: number;
  icon?: ReactElement;
  isSelected?: boolean;
  onClick?: (page: MenuPage, evt: React.MouseEvent) => void;
}

interface MenuSection {
  key: string;
  title?: string | ReactElement;
  path?: string;
  pages: MenuPage[];
  rightButtons?: ReactNode;
}

const Menu = (): ReactElement | null => {
  const { push, location } = useHistory();
  const { t: translate } = useTranslation();

  const user = useCurrentUser();
  const checklistItemsRemaining = useOnboardingChecklistRemaining();
  const unreadNotifications = useUnreadNotifications();
  const intercomUnreadNotifications = useIntercomUnreadNotificationCount();
  const { builder } = useBeaconRouter();

  const { open: openSupport } = useSupportContext();

  const { data: tenantData } = useGetTenantQuery({
    fetchPolicy: 'cache-only',
  });

  const bookmarks = useListBookmarks();

  const tenantName = tenantData?.getTenant.name;

  const {
    infiniteScroll,
    totalItems,
    result: teamResult,
  } = useListQueryWithInfiniteScroll<ListTeamsQuery, ListTeamsInput>({
    useQuery: useListTeamsQuery,
    queryOptions: {
      fetchPolicy: 'cache-only',
    },
    input: {
      active: true,
    },
  });

  const canCreateTeams = useHasPermission('create', 'team');
  const canReadPlans = useHasPermission('read', 'plan_subscription');

  const currentPath = location.pathname;

  const [selectedPageKey, setSelectedPageKey] = useState<string | undefined>();

  const hideSupport = useHasFeature('beacon-hide-support');

  const searchRowRenderer = useCallback<SearchRowRenderer<Team>>(
    ({ item, dismiss }) => {
      return (
        <IonMenuToggle key={item.id} autoHide={false}>
          <IonItem
            button={true}
            onClick={() => {
              dismiss();
              trackSearchResultClicked();
              push(builder.getTeamRoute(item.id));
            }}
          >
            <IonLabel>
              <h2>{item.name}</h2>
            </IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    },
    [builder, push]
  );
  const { showSearch } = useSearchPopover({
    options: {
      useQuery: useListTeamsQuery,
      input: { active: true, limit: 20 },
      queryOptions: {
        fetchPolicy: 'no-cache',
      },
    },
    rowRenderer: searchRowRenderer,
    placeholder: i18n.t('common:team_search'),
  });

  const sections: MenuSection[] = useMemo(() => {
    let sections: MenuSection[] = [];

    const additionalSection: MenuSection = {
      key: 'addl-sections',
      pages: [
        {
          key: 'home',
          title: translate('home'),
          path: builder.getHomeRoute(),
          badgeNumber: checklistItemsRemaining,
          cssId: 'home_menu_item',
        },
        {
          key: 'my-profile',
          title: translate('page_my_profile'),
          path: builder.getUserRoute('me'),
        },
        {
          key: 'notifications',
          title: translate('notifications'),
          path: builder.getNotificationsRoute(),
          badgeNumber: unreadNotifications,
        },
      ],
    };

    // if hide support is undefined then always show support. this is the safest
    // approach in case the feature system is down
    if (hideSupport === false || hideSupport === undefined) {
      additionalSection.pages.push({
        key: 'support',
        title: translate('support'),
        path: '#support',
        badgeNumber: intercomUnreadNotifications,
        onClick: async (_page, e) => {
          openSupport({
            source: SupportOpenSource.MENU,
            beforeOpen: async () => {
              if (isPlatform('capacitor')) {
                // allow the drawer to close before presenting the intercom messenger
                // is a less janky experience
                await sleep(500);
                e.stopPropagation();
                e.preventDefault();
              }
            },
          });
        },
      });
    }

    if (canReadPlans) {
      additionalSection.pages.push({
        key: 'billing',
        title: translate('billing'),
        path: builder.getBillingRoute(),
      });
    }

    if (user?.admin) {
      additionalSection.pages.push({
        key: 'adminland',
        title: translate('admin'),
        path: builder.getAdminRoute(),
      });
    }

    let starredSection: MenuSection | undefined;
    if (bookmarks?.length > 0) {
      starredSection = {
        key: 'starred',
        pages: bookmarks.map((b) => {
          const { url, title } = b;
          return {
            // this is to prevent collisions with keys generated for the team home pages
            key: `bookmarked-${url}`,
            path: url,
            title,
          };
        }),
        title: translate('starred'),
      };
    }

    const teamSections = createTeamsSection(
      builder,
      {
        items: teamResult.data?.listTeams?.items ?? [],
        total: totalItems,
      },
      canCreateTeams,
      () => {
        showSearch();
      }
    );

    sections.push(additionalSection);

    if (starredSection) {
      sections.push(starredSection);
    }

    sections = sections.concat(teamSections);

    const pages = sections
      .map((s) => s.pages)
      .reduce((prev, curr) => {
        return prev.concat(curr);
      }, []);

    // wrap page on click handlers to set selected page key

    pages.forEach((p) => {
      const originalOnClick = p.onClick;

      p.onClick = (page, evt) => {
        setSelectedPageKey(page.key);
        if (originalOnClick) {
          originalOnClick(page, evt);
        }
      };
    });

    for (const p of pages) {
      if (selectedPageKey) {
        if (selectedPageKey === p.key) {
          p.isSelected = true;
          break;
        }
      } else if (currentPath.startsWith(p.path)) {
        p.isSelected = true;
        break;
      }
    }

    return sections;
  }, [
    translate,
    builder,
    checklistItemsRemaining,
    unreadNotifications,
    hideSupport,
    canReadPlans,
    user?.admin,
    bookmarks,
    teamResult.data?.listTeams?.items,
    totalItems,
    canCreateTeams,
    intercomUnreadNotifications,
    showSearch,
    selectedPageKey,
    currentPath,
    openSupport,
  ]);

  return (
    <IonMenu className="main-nav-menu" contentId="main">
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonMenuToggle autoHide={false}>
            <IonItem
              routerLink={builder.getUserRoute('me')}
              routerDirection="none"
              detail={false}
              lines="none"
            >
              <Avatar
                avatarProps={{ slot: 'start' }}
                attachment={user?.avatarAttachment}
              />
              <IonText className="ion-padding-start tenant-name">
                <strong>{tenantName ?? 'No name'}</strong>
              </IonText>
            </IonItem>
          </IonMenuToggle>
          <IonButtons slot="end">
            <WorkspaceSwitcherModal>
              {({ showWorkspaceSwitcher }) => (
                <IonButton
                  slot="end"
                  shape="round"
                  onClick={() => showWorkspaceSwitcher()}
                >
                  <IonIcon
                    className="menu-action-icon"
                    icon={ellipsisHorizontalCircle}
                    size="large"
                  />
                </IonButton>
              )}
            </WorkspaceSwitcherModal>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="menu-content-container">
        <IonList lines="none">
          {sections.map((section) => (
            <Section key={section.key} section={section} />
          ))}
          {infiniteScroll}
        </IonList>
      </IonContent>
    </IonMenu>
  );
};

interface SectionProps {
  section: MenuSection;
}

const Section = (props: SectionProps): ReactElement => {
  const { section } = props;
  return (
    <div>
      {section.title && (
        <IonItem className="menu-section">
          <IonListHeader>{section.title}</IonListHeader>
          {section.rightButtons}
        </IonItem>
      )}
      {section.pages.map((page) => (
        <Page key={page.key} page={page} />
      ))}
    </div>
  );
};

interface PageProps {
  page: MenuPage;
}

const Page = (props: PageProps): ReactElement => {
  const { page } = props;
  const showBadge: boolean =
    page.badgeNumber && page.badgeNumber > 0 ? true : false;

  const { isSelected } = page;

  return (
    <IonMenuToggle autoHide={false}>
      <IonItem
        id={page.cssId}
        detail={false}
        routerLink={isSelected ? undefined : page.path}
        routerDirection="none"
        routerOptions={{}}
        className={isSelected ? 'selected' : 'unselected'}
        onClick={(evt) => {
          if (page.onClick) {
            page.onClick(page, evt);
          }
        }}
      >
        <IonLabel>{page.title}</IonLabel>
        {page.icon && page.icon}
        {showBadge && <IonBadge color="danger">{page.badgeNumber}</IonBadge>}
      </IonItem>
    </IonMenuToggle>
  );
};

const createTeamsSection = (
  builder: RouteBuilder,
  teams?: TeamList,
  canCreate = false,
  onSearch?: () => void
): MenuSection[] => {
  const pages: MenuPage[] = [];

  const path = canCreate ? builder.getNewTeamRoute() : undefined;
  const teamsSection: MenuSection[] = [
    {
      title: i18n.t('common:teams'),
      key: 'Teams',
      pages,
      path,
      rightButtons: (
        <IonButtons slot="end">
          <IonButton fill="clear" onClick={onSearch}>
            <IonIcon icon={search} />
          </IonButton>
          {path && (
            <IonMenuToggle autoHide={false}>
              <IonButton routerLink={path} routerDirection="none" fill="clear">
                <IonIcon icon={addCircleOutline} />
              </IonButton>
            </IonMenuToggle>
          )}
        </IonButtons>
      ),
    },
  ];

  if (teams) {
    teams.items?.map((team) => {
      const path = builder.getTeamRoute(team.id);

      pages.push({
        key: `team-${path}`,
        path,
        title: team.name,
      });
    });
  }
  return teamsSection;
};

export { Menu };
